/**
 * useDefaultDemonstratedFilter Hook
 *
 * Applies a default "Demonstrated" (status: ['O']) filter on the main
 * vulnerability table at the start of each browser session.
 *
 * This is a thin wrapper around the shared useDefaultStatusFilter hook,
 * configured for the vulnerability table's specific filter type and status value.
 *
 * IMPORTANT: This hook is ONLY for the main vulnerability table (/vulnerabilities).
 * It must NOT be used in drawer contexts, asset tabs, or any other table
 * that displays vulnerability data.
 */

import { Dispatch, SetStateAction } from 'react';

import { useDefaultStatusFilter } from '@/hooks/useDefaultStatusFilter';
import { Filter } from '@/hooks/useRisks';
import { omit } from '@/utils/lodash.util';
import { getCurrentTenantId } from '@/utils/storage/tenantScoping.util';

const applyDemonstratedFilter = (prev: Filter): Filter => ({
  ...prev,
  advancedFilters: {
    ...prev.advancedFilters,
    status: ['O'],
  },
  sort: prev.sort ?? { orderBy: 'priority' as const, descending: false },
});

const clearDemonstratedFilter = (prev: Filter): Filter => {
  const restAdvanced = omit(prev.advancedFilters, 'status');
  return {
    ...prev,
    advancedFilters:
      Object.keys(restAdvanced).length > 0 ? restAdvanced : undefined,
  };
};

export function useDefaultDemonstratedFilter({
  setFilters,
  filters,
  isFilteredEmpty,
  dataStatus,
  isFetching,
  enabled,
}: {
  setFilters: Dispatch<SetStateAction<Filter>>;
  filters: Filter;
  isFilteredEmpty: boolean;
  dataStatus: 'pending' | 'error' | 'success';
  isFetching: boolean;
  enabled: boolean;
}) {
  useDefaultStatusFilter<Filter>({
    sessionKey: `${getCurrentTenantId()}:vuln-default-demonstrated-filter`,
    applyFilter: applyDemonstratedFilter,
    clearFilter: clearDemonstratedFilter,
    setFilters,
    filters,
    isFilteredEmpty,
    dataStatus,
    isFetching,
    enabled,
  });
}
